import type { ReactNode } from 'react'
import { startTransition, useCallback } from 'react'

import { useTheme } from '../../../theme/ThemeContext'
import {
  themeDynamicIslandActionRowClass,
  themeDynamicIslandCompactTriggerClass,
  themeDynamicIslandExpandedBodyClass,
  themeDynamicIslandShellClass,
} from './dynamicIslandTokens'
import { ThemeDynamicIslandMorph } from './ThemeDynamicIslandMorph'

export { ThemeDynamicIslandMorph }

export type ThemeDynamicIslandShellProps = {
  angular?: boolean
  expanded: boolean
  children: ReactNode
  className?: string
}

/** 灵动岛外壳（圆角在 compact/expanded 间瞬时切换，见 index.css） */
export function ThemeDynamicIslandShell({
  angular: angularProp,
  expanded,
  children,
  className = '',
}: ThemeDynamicIslandShellProps) {
  const { theme } = useTheme()
  const angular = angularProp ?? (theme === 'pixel' || theme === 'geek')
  const glass = theme === 'glass'
  return (
    <div className={[themeDynamicIslandShellClass(angular, expanded, glass), className].filter(Boolean).join(' ')}>
      {children}
    </div>
  )
}

export type ThemeDynamicIslandMorphProps = {
  expanded: boolean
  compact: ReactNode
  expandedPane: ReactNode
}

/** 低优先级状态更新，减少展开时与队列轮询抢帧 */
export function useDynamicIslandExpand(setExpanded: (next: boolean) => void) {
  return useCallback(() => {
    startTransition(() => setExpanded(true))
  }, [setExpanded])
}

export type ThemeDynamicIslandCompactProps = {
  leading?: ReactNode
  title: string
  subtitle: string
  onExpand: () => void
  'aria-label'?: string
}

/** @deprecated 旧单条紧凑预览，请用业务 activity + {@link DynamicIslandSoloPreview} */
export function ThemeDynamicIslandCompact({
  leading,
  title,
  subtitle,
  onExpand,
  'aria-label': ariaLabel,
}: ThemeDynamicIslandCompactProps) {
  return (
    <button
      type="button"
      className={themeDynamicIslandCompactTriggerClass()}
      aria-label={ariaLabel ?? `${title}，${subtitle}，点击展开`}
      aria-expanded={false}
      onClick={(e) => {
        e.stopPropagation()
        onExpand()
      }}
    >
      {leading}
      <span className="min-w-0 truncate text-xs font-semibold leading-none text-ink">{title}</span>
      <span className="shrink-0 text-[11px] font-semibold tabular-nums leading-none text-ink-soft">
        {subtitle}
      </span>
    </button>
  )
}

export type ThemeDynamicIslandExpandedProps = {
  title: string
  description: string
  actions?: ReactNode
}

/** @deprecated 旧展开摘要，请用 {@link DynamicIslandExpandedSummary} */
export function ThemeDynamicIslandExpanded({ title, description, actions }: ThemeDynamicIslandExpandedProps) {
  return (
    <div className={themeDynamicIslandExpandedBodyClass()} role="region" aria-label={title}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold leading-snug text-ink">{title}</p>
        <p className="mt-1 text-xs leading-snug text-ink-soft">{description}</p>
      </div>
      {actions ? <ThemeDynamicIslandActionRow>{actions}</ThemeDynamicIslandActionRow> : null}
    </div>
  )
}

/** 展开态底部操作行（业务按钮请配 {@link themeDynamicIslandActionButtonClass}） */
export function ThemeDynamicIslandActionRow({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={[themeDynamicIslandActionRowClass(), className].filter(Boolean).join(' ')}>
      {children}
    </div>
  )
}
